import type { FormFieldDescriptor, TranslationKey, ValidationRuleDescriptor } from '@finance/contracts';
import { resolveTKey } from '../../i18n/resolve-key';
import type { ResolvedTranslationKey, StaticTranslator } from '../../i18n/static-translator';

type ValidationTranslator =
  | ((key: string, params?: Record<string, unknown>) => string)
  | StaticTranslator;

export interface ValidationError {
  messageKey: TranslationKey;
  params?: Record<string, unknown>;
}

export interface ValidationContext {
  values: Record<string, unknown>;
  getValue(fieldIdOrName: string): unknown;
  field: FormFieldDescriptor;
}

export type FieldValidator = (value: unknown, params: Record<string, unknown> | undefined, ctx: ValidationContext | undefined) => ValidationError | true;

const validators = new Map<string, FieldValidator>();

export function registerValidator(ruleId: string, validator: FieldValidator): void {
  validators.set(ruleId, validator);
}

export function getValidator(ruleId: string): FieldValidator | undefined {
  return validators.get(ruleId);
}

function tk(s: string): TranslationKey { return s as TranslationKey; }

registerValidator('required', (value) => {
  if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return { messageKey: tk('validation.required') };
  }
  return true;
});

registerValidator('accepted', (value) => {
  if (value !== true) {
    return { messageKey: tk('validation.accepted') };
  }
  return true;
});

registerValidator('min', (value, params) => {
  const min = params?.min as number;
  if (typeof value === 'number' && value < min) {
    return { messageKey: tk('validation.min'), params: { min } };
  }
  if (typeof value === 'string' && value !== '' && !isNaN(Number(value)) && Number(value) < min) {
    return { messageKey: tk('validation.min'), params: { min } };
  }
  return true;
});

registerValidator('max', (value, params) => {
  const max = params?.max as number;
  if (typeof value === 'number' && value > max) {
    return { messageKey: tk('validation.max'), params: { max } };
  }
  if (typeof value === 'string' && value !== '' && !isNaN(Number(value)) && Number(value) > max) {
    return { messageKey: tk('validation.max'), params: { max } };
  }
  return true;
});

registerValidator('minLength', (value, params) => {
  const min = params?.min as number;
  if (typeof value === 'string' && value.length < min) {
    return { messageKey: tk('validation.minLength'), params: { min } };
  }
  return true;
});

registerValidator('maxLength', (value, params) => {
  const max = params?.max as number;
  if (typeof value === 'string' && value.length > max) {
    return { messageKey: tk('validation.maxLength'), params: { max } };
  }
  return true;
});

registerValidator('integer', (value) => {
  if (value === '' || value === undefined || value === null) return true;
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(num)) {
    return { messageKey: tk('validation.integer') };
  }
  return true;
});

registerValidator('decimalPrecision', (value, params) => {
  const precision = params?.precision as number;
  if (value === '' || value === undefined || value === null) return true;
  const str = String(value);
  const dotIdx = str.indexOf('.');
  if (dotIdx >= 0 && str.length - dotIdx - 1 > precision) {
    return { messageKey: tk('validation.decimalPrecision'), params: { precision } };
  }
  return true;
});

registerValidator('email', (value) => {
  if (typeof value === 'string' && value) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(value)) {
      return { messageKey: tk('validation.email') };
    }
  }
  return true;
});

registerValidator('url', (value) => {
  if (typeof value === 'string' && value) {
    try {
      new URL(value);
    } catch {
      return { messageKey: tk('validation.url') };
    }
  }
  return true;
});

registerValidator('pattern', (value, params) => {
  const pattern = params?.pattern as string;
  if (typeof value === 'string' && value && pattern) {
    try {
      const re = new RegExp(pattern);
      if (!re.test(value)) {
        return { messageKey: tk('validation.pattern'), params: { pattern } };
      }
    } catch {
      return { messageKey: tk('validation.invalidPattern'), params: { pattern } };
    }
  }
  return true;
});

registerValidator('allowedValues', (value, params) => {
  const allowed = params?.values as unknown[];
  if (value !== undefined && value !== null && value !== '' && allowed && !allowed.includes(value)) {
    return { messageKey: tk('validation.allowedValues'), params: { values: allowed.join(', ') } };
  }
  return true;
});

registerValidator('dateRange', (value, params) => {
  if (!value || typeof value !== 'string') return true;
  const minDate = params?.minDate as string | undefined;
  const maxDate = params?.maxDate as string | undefined;
  const date = new Date(value);
  if (minDate && date < new Date(minDate)) {
    return { messageKey: tk('validation.dateMin'), params: { minDate } };
  }
  if (maxDate && date > new Date(maxDate)) {
    return { messageKey: tk('validation.dateMax'), params: { maxDate } };
  }
  return true;
});

registerValidator('fileType', (value, params) => {
  const accepted = params?.accepted as string[];
  if (!value || !accepted) return true;
  const files = Array.isArray(value) ? value : [value];
  for (const f of files) {
    if (f instanceof File && !accepted.includes(f.type)) {
      return { messageKey: tk('validation.fileType'), params: { accepted: accepted.join(', ') } };
    }
  }
  return true;
});

registerValidator('fileSize', (value, params) => {
  const maxBytes = params?.maxBytes as number;
  if (!value || !maxBytes) return true;
  const files = Array.isArray(value) ? value : [value];
  for (const f of files) {
    if (f instanceof File && f.size > maxBytes) {
      return { messageKey: tk('validation.fileSize'), params: { maxBytes } };
    }
  }
  return true;
});

registerValidator('crossField', (value, params, ctx) => {
  const other = params?.field as string;
  const operator = (params?.operator as string) ?? 'equals';
  const otherValue = ctx?.getValue?.(other) ?? params?.otherValue;
  if (value === undefined || value === null || value === '') return true;
  const satisfies = operator === 'equals' ? value === otherValue
    : operator === 'notEquals' ? value !== otherValue
    : operator === 'greaterThan' ? Number(value) > Number(otherValue)
    : operator === 'lessThan' ? Number(value) < Number(otherValue)
    : value === otherValue;
  if (!satisfies) {
    return { messageKey: tk('validation.crossField'), params: { field: other } };
  }
  return true;
});

registerValidator('registeredDomain', (value, params) => {
  const allowed = params?.domains as string[] | undefined;
  if (typeof value !== 'string' || !value || !allowed || allowed.length === 0) return true;
  const domain = value.split('@')[1] ?? value;
  if (!allowed.some((d) => domain === d || domain.endsWith(`.${d}`))) {
    return { messageKey: tk('validation.registeredDomain'), params: { domains: allowed.join(', ') } };
  }
  return true;
});

registerValidator('branchParentActive', (value) => {
  if (value === undefined || value === null || value === '') return true;
  return true;
});

function resolveMessage(
  rule: { messageKey?: TranslationKey; params?: Record<string, unknown> },
  result: ValidationError,
): ValidationError {
  return {
    messageKey: rule.messageKey ?? result.messageKey,
    params: { ...result.params, ...rule.params },
  };
}

export function validateField(
  field: FormFieldDescriptor,
  value: unknown,
  ctx?: ValidationContext,
): ValidationError | true {
  const rules = field.validation?.rules;
  if (!rules) return true;

  for (const rule of rules) {
    const validator = getValidator(rule.ruleId as string);
    if (validator) {
      const result = validator(value, rule.params, ctx);
      if (result !== true) {
        return resolveMessage(rule, result);
      }
    }
  }

  return true;
}

interface RHFValidationRules {
  required?: boolean | string | { value: boolean; message: string };
  min?: { value: number; message: string };
  max?: { value: number; message: string };
  minLength?: { value: number; message: string };
  maxLength?: { value: number; message: string };
  pattern?: { value: RegExp; message: string };
  validate?: (value: unknown) => true | string;
}

function translateRuleMessage(
  rule: ValidationRuleDescriptor,
  defaultKey: TranslationKey,
  params: Record<string, unknown>,
  t?: ValidationTranslator,
): string {
  const key = rule.messageKey ?? defaultKey;
  return t ? t(resolveTKey(key) as ResolvedTranslationKey, { ...rule.params, ...params }) : key;
}

export function buildValidationRules(
  field: FormFieldDescriptor,
  t?: ValidationTranslator,
  formValues?: Record<string, unknown>,
): RHFValidationRules {
  const rules = field.validation?.rules ?? [];
  const rhfRules: RHFValidationRules = {};

  const getValue = (fieldIdOrName: string): unknown => (formValues ?? {})[fieldIdOrName];

  const makeValidateFn = (
    validator: FieldValidator,
    rule: ValidationRuleDescriptor,
    existingValidate?: (value: unknown) => true | string,
  ): ((value: unknown) => true | string) => {
    return (value: unknown) => {
      if (existingValidate) {
        const prev = existingValidate(value);
        if (prev !== true) return prev;
      }
      const ctx: ValidationContext = { values: formValues ?? {}, getValue, field };
      const result = validator(value, rule.params, ctx);
      if (result !== true) {
        const error = resolveMessage(rule, result);
        return t
          ? t(resolveTKey(error.messageKey) as ResolvedTranslationKey, error.params)
          : (error.messageKey as string);
      }
      return true;
    };
  };

  for (const rule of rules) {
    const ruleId = rule.ruleId as string;
    const params = rule.params ?? {};

    switch (ruleId) {
      case 'required': {
        const message = translateRuleMessage(rule, tk('validation.required'), {}, t);
        rhfRules.required = { value: true, message };
        break;
      }
      case 'min': {
        const min = params.min as number;
        rhfRules.min = { value: min, message: translateRuleMessage(rule, tk('validation.min'), { min }, t) };
        break;
      }
      case 'max': {
        const max = params.max as number;
        rhfRules.max = { value: max, message: translateRuleMessage(rule, tk('validation.max'), { max }, t) };
        break;
      }
      case 'minLength': {
        const min = params.min as number;
        rhfRules.minLength = { value: min, message: translateRuleMessage(rule, tk('validation.minLength'), { min }, t) };
        break;
      }
      case 'maxLength': {
        const max = params.max as number;
        rhfRules.maxLength = { value: max, message: translateRuleMessage(rule, tk('validation.maxLength'), { max }, t) };
        break;
      }
      case 'pattern': {
        const pattern = params.pattern as string;
        try {
          rhfRules.pattern = { value: new RegExp(pattern), message: translateRuleMessage(rule, tk('validation.pattern'), { pattern }, t) };
        } catch {
          rhfRules.validate = makeValidateFn(
            () => ({ messageKey: tk('validation.invalidPattern'), params: { pattern } }),
            rule,
            rhfRules.validate,
          );
        }
        break;
      }
      case 'integer':
      case 'decimalPrecision':
      case 'email':
      case 'url':
      case 'allowedValues':
      case 'dateRange':
      case 'fileType':
      case 'fileSize':
      case 'crossField':
      case 'registeredDomain':
      case 'branchParentActive': {
        const validator = getValidator(ruleId);
        if (validator) {
          rhfRules.validate = makeValidateFn(validator, rule, rhfRules.validate);
        } else {
          rhfRules.validate = makeValidateFn(
            () => ({ messageKey: tk('validation.unknownValidator'), params: { ruleId } }),
            rule,
            rhfRules.validate,
          );
        }
        break;
      }
      default: {
        const validator = getValidator(ruleId);
        if (validator) {
          rhfRules.validate = makeValidateFn(validator, rule, rhfRules.validate);
        } else {
          rhfRules.validate = makeValidateFn(
            () => ({ messageKey: tk('validation.unknownValidator'), params: { ruleId } }),
            rule,
            rhfRules.validate,
          );
        }
        break;
      }
    }
  }

  return rhfRules;
}

export function isFieldRequired(field: FormFieldDescriptor): boolean {
  return !!field.validation?.rules?.some((r) => (r.ruleId as string) === 'required');
}
